import { Length as DomainLength } from '@igus/echain-shared-quantities';
import { GraphQlPriceType } from '@models/graphql';
import {
  CHAINFLEX_MAX_BUY_LENGTH,
  CHAINFLEX_MAX_BUY_QUANTITY,
  CHAINFLEX_MIN_BUY_LENGTH,
  CHAINFLEX_MIN_BUY_QUANTITY,
  DEFAULT_CURRENCY,
  DRYLIN_T_MAX_BUY_QUANTITY,
  DRYLIN_T_MIN_BUY_QUANTITY,
  E_CHAIN_MAX_BUY_LENGTH,
  E_CHAIN_MAX_BUY_QUANTITY,
  E_CHAIN_MIN_BUY_LENGTH,
  E_CHAIN_MIN_BUY_QUANTITY,
  LENGTH_AND_PIECE_WITHOUT_COPPER_MAX_BUY_LENGTH,
  LENGTH_AND_PIECE_WITHOUT_COPPER_MAX_BUY_QUANTITY,
  LENGTH_AND_PIECE_WITHOUT_COPPER_MIN_BUY_LENGTH,
  LENGTH_AND_PIECE_WITHOUT_COPPER_MIN_BUY_QUANTITY,
  PRICE_FORMATTER_OPTIONS,
  TRIBOTAPE_MAX_BUY_QUANTITY,
  TRIBOTAPE_MIN_BUY_QUANTITY,
  VAT_FACTORS,
} from '@libs/constants';
import { sortByParam } from '@libs/comparer.helper';
import type { AssociatedProductPrice, SharedProductPagePropsPrices } from '@product/ProductPage/ProductPage';
import type { PriceFragmentEnhanced, ProductFragmentEnhanced } from '@models/enhanced-fragments';
import type { AlternativeProductProps } from '@product/AlternativeProduct/AlternativeProduct';
import type { SuitableProductProps } from '@product/SuitableProduct/SuitableProduct';
import { convertPriceToCent } from './utils';
import type { LengthConfig } from '@libs/product.helper';
import type { ArticleDetailResponse } from '@models/api/shopping-cart.types';
import type { EchainPartsListItem } from '@hooks/states/echainPartsListSlice';
import { LengthCode, type UnitCode } from '@icommerce/units';

export enum PriceQuantityUnit {
  PIECE = 'piece',
  LINK = 'link',
  METER = 'm',
  MILLIMETER = 'mm',
  FEET = 'ft',
  KILOGRAM = 'kg',
  INCH = 'in',
}

const sortPriceByDiscountThreshold = (priceA: PriceFragmentEnhanced, priceB: PriceFragmentEnhanced) =>
  (priceA.discount_threshold !== undefined && priceA.discount_threshold !== null
    ? priceA.discount_threshold
    : Number.MAX_VALUE) <
  (priceB.discount_threshold !== undefined && priceB.discount_threshold !== null
    ? priceB.discount_threshold
    : Number.MAX_VALUE)
    ? -1
    : 1;

export const getProductBasePrice = (product: ProductFragmentEnhanced): number => {
  // if there exists a price with unit "piece", use this as base price (for price_type LengthAndPiece and Piece), if none exists, we must use price with unit <> "piece" as baseprice (usually "m")
  // if discount_threshold is null or undefined, use Number.MAX_VALUE to ensure it is sorted as last item in the list
  const pieceBasePriceTiers = product.prices
    .filter(price => price.unit === PriceQuantityUnit.PIECE)
    .sort(sortPriceByDiscountThreshold);
  if (pieceBasePriceTiers.length > 0) {
    return pieceBasePriceTiers[0].price ? pieceBasePriceTiers[0].price * 100 : 0;
  }
  const lengthPriceTiers = product.prices
    .filter(price => price.unit !== PriceQuantityUnit.PIECE)
    .sort(sortPriceByDiscountThreshold);
  return lengthPriceTiers?.length > 0 && lengthPriceTiers[0].price && lengthPriceTiers[0].price > 0
    ? lengthPriceTiers[0].price * 100
    : 0;
};

export const getAssociationProductDiscountPrice = (prices: PriceFragmentEnhanced[], amount: number): number => {
  // ensure with [...prices] that the prices are not read only => "Cannot assign to read only property of object
  const basePriceTiers = [...prices].sort(sortByParam<PriceFragmentEnhanced>(item => item.discount_threshold, true));
  const discountTier = basePriceTiers.find(
    item =>
      (item.discount_threshold !== undefined && item.discount_threshold !== null
        ? item.discount_threshold
        : Number.MAX_VALUE) <= amount
  );
  const basePrice = basePriceTiers?.length > 0 && basePriceTiers[0].price ? basePriceTiers[0].price : 0;
  const discountedPrice = discountTier !== undefined ? discountTier.price : undefined;

  const finalDiscountPriceInCent = (discountedPrice !== undefined ? discountedPrice : basePrice) * 100;
  const finalDiscountPriceRounded = Math.round(finalDiscountPriceInCent) / 100;
  return finalDiscountPriceRounded;
};

export const getProductLengthPrice = (product: ProductFragmentEnhanced, priceType: GraphQlPriceType) => {
  // though GraphQlPriceType.Length also has length prices, we only use them for the priceType LengthAndPiece and
  // LengthAndPieceWithoutCopper because it is already calculated in the base price
  switch (priceType) {
    case GraphQlPriceType.LengthAndPiece:
    case GraphQlPriceType.LengthAndPieceWithoutCopper: {
      const lengthPriceTiers = product.prices
        .filter(price => price.unit !== PriceQuantityUnit.PIECE)
        .sort(sortPriceByDiscountThreshold);
      return lengthPriceTiers.length > 0 && lengthPriceTiers[0].price ? lengthPriceTiers[0].price * 100 : 0;
    }
    case GraphQlPriceType.Length:
    case GraphQlPriceType.Piece:
    case GraphQlPriceType.PieceWithCopper:
    case GraphQlPriceType.LengthWithoutCopper:
    case GraphQlPriceType.LengthWithCuttingCostWithoutCopper:
    case GraphQlPriceType.ChainLink:
    case GraphQlPriceType.ChainLength:
    case GraphQlPriceType.PieceWithChainLengthDependency:
    case GraphQlPriceType.LeadScrewLength:
    case GraphQlPriceType.TribotapeLength:
      return undefined;
  }
};

export const getPriceDisplayTypeForOrderType = (orderType: string) => {
  switch (orderType) {
    case '100':
      return GraphQlPriceType.Piece;
    case '110':
      return GraphQlPriceType.PieceWithCopper;
    case '200':
      return GraphQlPriceType.LengthWithoutCopper;
    case '210':
      return GraphQlPriceType.Length;
    case '220':
      return GraphQlPriceType.LengthAndPiece;
    case '1210':
    case '1510':
      return GraphQlPriceType.LengthWithCuttingCostWithoutCopper;
    case '1260':
      return GraphQlPriceType.LengthAndPieceWithoutCopper;
    case '1270':
      return GraphQlPriceType.LeadScrewLength;
    case '300':
      return GraphQlPriceType.PieceWithChainLengthDependency;
    case '400':
    case '10400':
      return GraphQlPriceType.ChainLink;
    case '401':
      return GraphQlPriceType.ChainLength;
    case '1520':
      return GraphQlPriceType.TribotapeLength;
    default:
      throw new Error(`Unknown orderType ${orderType}`);
  }
};

const getPriceFormatterOptions = (locale: string, currencyCode: string): Intl.NumberFormatOptions => {
  const options: Intl.NumberFormatOptions = PRICE_FORMATTER_OPTIONS[locale] ?? PRICE_FORMATTER_OPTIONS.default;

  return {
    currency: currencyCode,
    ...options,
  };
};

/**
 * format a given price (in cent) to a string matching the locale preferences
 */
export const formatPrice = (price: number, locale: string, currencyCode: string = DEFAULT_CURRENCY): string =>
  new Intl.NumberFormat(locale, getPriceFormatterOptions(locale, currencyCode)).format(price);

/**
 * format price with a fallback
 * returns an object containing prices with and without discounts and the total discount
 */
export const displayPrice = (
  price: number | undefined,
  currencyCode: string | undefined,
  locale: string | undefined,
  isCentPrice = false,
  fallback = ''
): string => {
  if (!price || !currencyCode || !locale) {
    return fallback;
  }
  return formatPrice(isCentPrice ? (price || 0) / 100 : price, locale, currencyCode);
};

export const getVATCalculatedPrice = (locale: string, totalCentDiscountPrice: number) => {
  if (!VAT_FACTORS[locale]) {
    throw Error('VAT factor for locale is not defined');
  }
  return totalCentDiscountPrice * +VAT_FACTORS[locale];
};

export type TotalProductPrice = {
  totalCentPrice?: number;
  totalCentDiscountPrice?: number;
  totalCentDiscount?: number;
  unitCentDiscountPrice?: number;
};

/**
 * calculates prices for a chainflexc product by incorporating price levels (Staffelpreise)
 * returns an object containing prices with and without discounts and the total discount
 * @return {
 *   totalCentPrice?  total price in cent without any discount
 *   totalCentDiscountPrice?  total price in cent based on the quantity discount price levels  (Staffelpreise)
 *   totalCentDiscount?  total discount in cent
 *   unitCentDiscountPrice?  unit price with quantity discount
 * }
 */
function calculateTotalChainflexPrice(
  units: number, // represents length of cable in meters in this calculation
  unitCentPrice: number,
  pieces: number,
  quantityDiscounts?: PriceFragmentEnhanced[]
): TotalProductPrice {
  // Cause we calculate a price of a cable here, we can handle the units as length.
  // We have to use Math.ceil() here to remove fractions of mm of the length value
  // and use the next larger full mm-value to calculate the price of the cable.
  // Otherwise the calculated price can differ from the price in the cart by some cents
  const ceiledUnits = DomainLength.mm(Math.ceil(units * 1000)).m;

  if (
    !Number.isInteger(pieces) ||
    ceiledUnits < CHAINFLEX_MIN_BUY_LENGTH ||
    ceiledUnits > CHAINFLEX_MAX_BUY_LENGTH ||
    pieces < CHAINFLEX_MIN_BUY_QUANTITY ||
    pieces > CHAINFLEX_MAX_BUY_QUANTITY
  ) {
    return {};
  }

  // find price per unit of correct discount level (=Staffelpreis)
  const discountTier = quantityDiscounts
    ? [...quantityDiscounts]
        .sort(sortByParam<PriceFragmentEnhanced>(item => item.discount_threshold, true))
        .find(
          item =>
            (item.discount_threshold !== undefined && item.discount_threshold !== null
              ? item.discount_threshold
              : Number.MAX_VALUE) <=
            ceiledUnits * pieces
        )
    : undefined;
  const quantityLevelCentPrice = discountTier !== undefined ? discountTier.price * 100 : undefined;
  const unitCentDiscountPrice = quantityLevelCentPrice !== undefined ? quantityLevelCentPrice : unitCentPrice;
  const totalCentPrice = Math.round(unitCentPrice * ceiledUnits * pieces);
  const totalCentDiscountPrice = unitCentDiscountPrice
    ? Math.round(unitCentDiscountPrice * ceiledUnits * pieces)
    : undefined;
  const totalCentDiscount =
    totalCentDiscountPrice && totalCentDiscountPrice > 0
      ? Math.round(totalCentPrice - totalCentDiscountPrice)
      : undefined;

  return {
    totalCentPrice,
    totalCentDiscountPrice,
    totalCentDiscount,
    unitCentDiscountPrice: Math.round(unitCentDiscountPrice),
  };
}

export const calculateTotalLengthWithCuttingCostWithoutCopperPrice = (
  length: number, // length in mm
  unitCentPrice: number,
  pieces: number,
  quantityDiscounts?: PriceFragmentEnhanced[]
) => {
  // we have to multiply and to divide by 1000 in the following lines, cause the prices we get from the price-API
  // are METER-prices, but we have to handle them as MILLIMETER-prices in the frontend

  // find price per unit of correct discount level (=Staffelpreis)
  const discountTier = quantityDiscounts
    ? [...quantityDiscounts]
        .sort(sortByParam<PriceFragmentEnhanced>(item => item.discount_threshold, true))
        .find(
          item =>
            (item.discount_threshold !== undefined && item.discount_threshold !== null
              ? item.discount_threshold * 1000
              : Number.MAX_VALUE) <=
            length * pieces
        )
    : undefined;
  const quantityLevelCentPrice = discountTier !== undefined ? discountTier.price * 100 : undefined;
  const unitCentDiscountPrice = quantityLevelCentPrice !== undefined ? quantityLevelCentPrice : unitCentPrice;
  const totalCentPrice = Math.round(unitCentPrice * length * pieces) / 1000;
  const totalCentDiscountPrice = unitCentDiscountPrice
    ? Math.round(unitCentDiscountPrice * length * pieces) / 1000
    : undefined;
  const totalCentDiscount =
    totalCentDiscountPrice && totalCentDiscountPrice > 0
      ? Math.round(totalCentPrice - totalCentDiscountPrice)
      : undefined;

  return {
    totalCentPrice,
    totalCentDiscountPrice,
    totalCentDiscount,
    unitCentDiscountPrice: Math.round(unitCentDiscountPrice),
  };
};

/**
 *
 * @param length Length in mm
 * @param unitCentPrice The price per unit in cents which is calculated in getProductBasePrice()
 * @param pieces Number of pieces
 * @param lengthConfig see getProductLengthConfig()
 * @param quantityDiscounts
 */
export const calculateTotalTribotapePrice = (
  length: number,
  unitCentPrice: number,
  pieces: number,
  lengthConfig: LengthConfig,
  quantityDiscounts: PriceFragmentEnhanced[]
): TotalProductPrice => {
  if (
    !Number.isInteger(pieces) ||
    length < lengthConfig.min ||
    length > lengthConfig.max ||
    pieces < TRIBOTAPE_MIN_BUY_QUANTITY ||
    pieces > TRIBOTAPE_MAX_BUY_QUANTITY
  ) {
    return {};
  }
  return calculateTotalLengthWithCuttingCostWithoutCopperPrice(length, unitCentPrice, pieces, quantityDiscounts);
};

function calculateTotalPiecePrice(
  unitCentPrice: number,
  pieces: number,
  quantityDiscounts?: PriceFragmentEnhanced[]
): TotalProductPrice {
  if (!Number.isInteger(pieces) || pieces < CHAINFLEX_MIN_BUY_QUANTITY || pieces > CHAINFLEX_MAX_BUY_QUANTITY) {
    return {};
  }

  const roundedUnitCentPrice = Math.round(unitCentPrice);

  // find price per unit of correct discount level (=Staffelpreis)
  const quantityLevelCentPriceFragment = quantityDiscounts
    ? [...quantityDiscounts]
        .sort(sortByParam<PriceFragmentEnhanced>(item => item.discount_threshold, true))
        .find(
          item =>
            (item.discount_threshold !== null && item.discount_threshold !== undefined
              ? item.discount_threshold
              : Number.MAX_VALUE) <= pieces
        )
    : undefined;
  const quantityLevelCentPrice =
    quantityLevelCentPriceFragment && quantityLevelCentPriceFragment.price
      ? Math.round(quantityLevelCentPriceFragment.price * 100)
      : undefined;

  const unitCentDiscountPrice = quantityLevelCentPrice !== undefined ? quantityLevelCentPrice : roundedUnitCentPrice;
  const totalCentPrice = roundedUnitCentPrice * pieces;
  const totalCentDiscountPrice = unitCentDiscountPrice ? unitCentDiscountPrice * pieces : undefined;
  const totalCentDiscount =
    totalCentDiscountPrice && totalCentDiscountPrice > 0 ? totalCentPrice - totalCentDiscountPrice : undefined;

  return { totalCentPrice, totalCentDiscountPrice, totalCentDiscount, unitCentDiscountPrice };
}

const calculateTotalTriflexPrice = (
  unitBasePriceInCent: number,
  pieces: number,
  quantityDiscounts: PriceFragmentEnhanced[],
  links: number
): TotalProductPrice => calculateTotalPiecePrice(unitBasePriceInCent, pieces * (links || 1), quantityDiscounts);

export const calculateTotalDrylinTPrice = (
  length: number, // length in mm
  unitCentPrice: number,
  pieces: number,
  lengthConfig: LengthConfig,
  quantityDiscounts?: PriceFragmentEnhanced[]
): TotalProductPrice => {
  if (
    !Number.isInteger(pieces) ||
    length < lengthConfig.min ||
    length > lengthConfig.max ||
    pieces < DRYLIN_T_MIN_BUY_QUANTITY ||
    pieces > DRYLIN_T_MAX_BUY_QUANTITY
  ) {
    return {};
  }

  return calculateTotalLengthWithCuttingCostWithoutCopperPrice(length, unitCentPrice, pieces, quantityDiscounts);
};

/**
 * calculates the copper surcharge in price per m  e.g. 0,07 €/m
 * @param copperIndexKgPerKm  the copper index of the product in KILOGRAM_PER_KILOMETER
 * @param copperPricePerKg the daily copper price per weight e.g. 146€/100kg
 * @param copperWeightInKg the weight of the daily copper price egg. in 146€/100kg
 */
export const calculateCopperSurchargePerM = (
  copperIndexKgPerKm: number,
  copperPricePerKg: number,
  copperWeightInKg: number
): number | undefined => {
  let copperIdxKgPerKm = copperIndexKgPerKm;
  // minimal value for copperIndexKgPerKm is 10
  if (copperIdxKgPerKm < 10) {
    copperIdxKgPerKm = 10;
  }

  // constant defined by the business unit
  const COPPER_BASE_PRICE_PER_100KG = 150;

  if (!copperIdxKgPerKm || !copperPricePerKg || !copperWeightInKg) {
    return undefined;
  }
  return parseFloat(
    (((copperPricePerKg / copperWeightInKg - COPPER_BASE_PRICE_PER_100KG / 100) * copperIdxKgPerKm) / 1000).toFixed(2)
  );
};

/**
 * returns the price and the quantity discounts from an article detail response for a single product
 * */
export const mapArticleDetailResponseToPrices = (
  res: ArticleDetailResponse<'number'>,
  priceType: GraphQlPriceType,
  countryCode: string
): AssociatedProductPrice[] => {
  const prices = [];

  for (const article of res.articles) {
    const firstQuantityRange: number[] = article?.priceScales[0]?.prices;
    const baseUnitCentPrice = firstQuantityRange?.length > 0 ? firstQuantityRange[0] * 100 : null;
    const lengthUnitCentPrice = firstQuantityRange?.length > 1 ? firstQuantityRange[1] * 100 : null;

    const quantityDiscounts = article?.priceScales?.length
      ? article.priceScales.flatMap(discount => {
          switch (priceType) {
            case GraphQlPriceType.LengthAndPiece:
              return [
                {
                  price: discount.prices[0],
                  unit: PriceQuantityUnit.PIECE,
                  unit_multiplier: 1,
                  discount_threshold: discount.quantity,
                  country_code: countryCode,
                  currency_code: res.currency,
                },
                {
                  price: discount.prices[1],
                  unit: PriceQuantityUnit.METER,
                  unit_multiplier: 1,
                  discount_threshold: discount.quantity,
                  country_code: countryCode,
                  currency_code: res.currency,
                },
              ];
            case GraphQlPriceType.LengthAndPieceWithoutCopper:
              return [
                {
                  price: discount.prices[0],
                  unit: PriceQuantityUnit.PIECE,
                  unit_multiplier: 1,
                  discount_threshold: discount.quantity,
                  country_code: countryCode,
                  currency_code: res.currency,
                },
                {
                  price: discount.prices[1],
                  unit: PriceQuantityUnit.MILLIMETER,
                  unit_multiplier: 100,
                  discount_threshold: discount.quantity,
                  country_code: countryCode,
                  currency_code: res.currency,
                },
              ];
            case GraphQlPriceType.LeadScrewLength:
              return [
                {
                  price: discount.prices[0],
                  unit: PriceQuantityUnit.MILLIMETER,
                  unit_multiplier: 100,
                  discount_threshold: discount.quantity,
                  country_code: countryCode,
                  currency_code: res.currency,
                },
              ];
            case GraphQlPriceType.LengthWithCuttingCostWithoutCopper:
              return {
                price: discount.prices[0],
                unit: PriceQuantityUnit.METER,
                unit_multiplier: 1,
                discount_threshold: discount.quantity,
                country_code: countryCode,
                currency_code: res.currency,
              };
            case GraphQlPriceType.Length:
              return {
                price: discount.prices[0],
                unit: PriceQuantityUnit.METER,
                unit_multiplier: 1,
                discount_threshold: discount.quantity,
                country_code: countryCode,
                currency_code: res.currency,
              };
            case GraphQlPriceType.ChainLength:
              return {
                price: discount.prices[0],
                unit: PriceQuantityUnit.METER,
                unit_multiplier: 1,
                discount_threshold: discount.quantity,
                country_code: countryCode,
                currency_code: res.currency,
              };
            case GraphQlPriceType.TribotapeLength:
              return {
                price: discount.prices[0],
                unit: PriceQuantityUnit.MILLIMETER,
                discount_threshold: discount.quantity,
                country_code: countryCode,
                currency_code: res.currency,
              };
            case GraphQlPriceType.Piece:
            case GraphQlPriceType.PieceWithCopper:
            case GraphQlPriceType.LengthWithoutCopper:
            case GraphQlPriceType.ChainLink:
            case GraphQlPriceType.PieceWithChainLengthDependency:
              return {
                price: discount.prices[0],
                unit: PriceQuantityUnit.PIECE,
                unit_multiplier: 1,
                discount_threshold: discount.quantity,
                country_code: countryCode,
                currency_code: res.currency,
              };
          }
        })
      : [];

    prices.push({
      id: article.articleNumber,
      baseUnitCentPrice,
      lengthUnitCentPrice,
      quantityDiscounts,
    });
  }

  return prices as AssociatedProductPrice[];
};

export function calculateLengthUnitPrice(basePrice: number, lengthPrice: number, lengthUnit: number) {
  // if price is -1 or 0 for example (Auf Anfrage), just return 0 without calculating and avoid running into errors in the future
  if (!basePrice || !lengthPrice || basePrice <= 0 || lengthPrice <= 0) {
    return 0;
  }
  return Math.round((basePrice + lengthPrice * lengthUnit) * 100) / 100;
}

function calculateTotalLengthPiecePrice(
  priceType: GraphQlPriceType.LengthAndPiece | GraphQlPriceType.LengthAndPieceWithoutCopper,
  units: number,
  unitCentPrice: number,
  pieces: number,
  unitLengthPriceInCent?: number,
  quantityDiscounts?: PriceFragmentEnhanced[]
): TotalProductPrice {
  if (!unitLengthPriceInCent || !Number.isInteger(pieces)) {
    return {};
  }

  let unitLengthPriceInCentValue = unitLengthPriceInCent;

  switch (priceType) {
    case GraphQlPriceType.LengthAndPiece:
      if (
        units < CHAINFLEX_MIN_BUY_LENGTH ||
        units > CHAINFLEX_MAX_BUY_LENGTH ||
        pieces < CHAINFLEX_MIN_BUY_QUANTITY ||
        pieces > CHAINFLEX_MAX_BUY_QUANTITY
      ) {
        return {};
      }
      break;
    case GraphQlPriceType.LengthAndPieceWithoutCopper:
      if (
        units < LENGTH_AND_PIECE_WITHOUT_COPPER_MIN_BUY_LENGTH ||
        units > LENGTH_AND_PIECE_WITHOUT_COPPER_MAX_BUY_LENGTH ||
        pieces < LENGTH_AND_PIECE_WITHOUT_COPPER_MIN_BUY_QUANTITY ||
        pieces > LENGTH_AND_PIECE_WITHOUT_COPPER_MAX_BUY_QUANTITY
      ) {
        return {};
      }
      break;
  }

  // find price per unit of correct discount level (=Staffelpreis)
  const quantityLevels = quantityDiscounts
    ? [...quantityDiscounts]
        .sort(sortByParam<PriceFragmentEnhanced>(item => item.discount_threshold, true))
        .filter(
          item =>
            (item.discount_threshold !== null && item.discount_threshold !== undefined
              ? item.discount_threshold
              : Number.MAX_VALUE) <= pieces
        )
    : undefined;
  const quantityLevelBaseCentPrice =
    quantityLevels && quantityLevels.length
      ? convertPriceToCent(quantityLevels.find(price => price.unit === PriceQuantityUnit.PIECE)?.price)
      : undefined;
  let quantityLevelLengthCentPrice =
    quantityLevels && quantityLevels.length
      ? convertPriceToCent(quantityLevels.find(price => price.unit !== PriceQuantityUnit.PIECE)?.price)
      : undefined;

  // TODO database values should be used instead of hardcoded values
  if (priceType === GraphQlPriceType.LengthAndPieceWithoutCopper) {
    // price is based on 100 mm, so we need to divide by 100 for correct length price
    quantityLevelLengthCentPrice = quantityLevelLengthCentPrice
      ? quantityLevelLengthCentPrice / 100
      : quantityLevelLengthCentPrice;
    unitLengthPriceInCentValue /= 100;
  }

  // Math.round is necessary due to floating point precision bug resulting in values like 11.9999999998 instead of 12
  const unitCentDiscountPrice =
    quantityLevelBaseCentPrice != null && quantityLevelLengthCentPrice != null
      ? calculateLengthUnitPrice(quantityLevelBaseCentPrice, quantityLevelLengthCentPrice, units)
      : calculateLengthUnitPrice(unitCentPrice, unitLengthPriceInCentValue, units);
  const totalCentPrice =
    (calculateLengthUnitPrice(unitCentPrice, unitLengthPriceInCentValue, units) * pieces * 100) / 100;
  const totalCentDiscountPrice =
    unitCentDiscountPrice && unitCentDiscountPrice > 0
      ? Math.round(unitCentDiscountPrice * pieces * 100) / 100
      : undefined;
  const totalCentDiscount =
    totalCentDiscountPrice && totalCentDiscountPrice > 0 ? totalCentPrice - totalCentDiscountPrice : undefined;

  return { totalCentPrice, totalCentDiscountPrice, totalCentDiscount, unitCentDiscountPrice };
}

const findApplicableDiscountPrice = (
  totalLengthForDiscount: number,
  quantityDiscounts?: PriceFragmentEnhanced[]
): number | undefined => {
  if (!quantityDiscounts?.length) {
    return undefined;
  }

  const applicableTier = [...quantityDiscounts]
    .sort((a, b) => (b.discount_threshold ?? 0) - (a.discount_threshold ?? 0))
    .find(tier => totalLengthForDiscount >= (tier.discount_threshold ?? 0));

  if (applicableTier?.price && applicableTier.price > 0) {
    return applicableTier.price * 100; // Return price in cents
  }

  return undefined;
};

const calculateMetricPrice = ({
  pieces,
  length_mm,
  unitCentPricePerMeter,
  quantityDiscounts,
}: {
  pieces: number;
  length_mm: number;
  unitCentPricePerMeter: number;
  quantityDiscounts?: PriceFragmentEnhanced[];
}): TotalProductPrice => {
  // The price is per meter, but length is in mm. Convert price to be per mm.
  const unitCentPricePerMM = unitCentPricePerMeter / 1000;
  const totalCentPrice = unitCentPricePerMM * length_mm * pieces;

  // For discounts, the threshold is based on total meters.
  const totalLengthInMeters = (length_mm / 1000) * pieces;
  const discountTierCentPricePerMeter = findApplicableDiscountPrice(totalLengthInMeters, quantityDiscounts);

  if (discountTierCentPricePerMeter === undefined) {
    return { totalCentPrice };
  }

  const unitCentDiscountPricePerMeter = discountTierCentPricePerMeter;
  const unitCentDiscountPricePerMM = unitCentDiscountPricePerMeter / 1000;

  const totalCentDiscountPrice = unitCentDiscountPricePerMM * length_mm * pieces;
  const totalCentDiscount = totalCentPrice - totalCentDiscountPrice;

  return {
    totalCentPrice,
    totalCentDiscountPrice,
    totalCentDiscount,
    unitCentDiscountPrice: unitCentDiscountPricePerMeter,
  };
};

const calculateImperialPrice = ({
  pieces,
  length_ft,
  unitCentPricePerFoot,
  quantityDiscounts,
}: {
  pieces: number;
  length_ft: number;
  unitCentPricePerFoot: number;
  quantityDiscounts?: PriceFragmentEnhanced[];
}): TotalProductPrice => {
  // Price and length are in the same unit (feet), so the calculation is direct.
  const totalCentPrice = unitCentPricePerFoot * length_ft * pieces;

  // For discounts, the threshold is based on total feet.
  const totalLengthInFeet = length_ft * pieces;
  const discountTierCentPricePerFoot = findApplicableDiscountPrice(totalLengthInFeet, quantityDiscounts);

  if (discountTierCentPricePerFoot === undefined) {
    return { totalCentPrice };
  }

  const unitCentDiscountPrice = discountTierCentPricePerFoot;
  const totalCentDiscountPrice = unitCentDiscountPrice * length_ft * pieces;
  const totalCentDiscount = totalCentPrice - totalCentDiscountPrice;

  return {
    totalCentPrice,
    totalCentDiscountPrice,
    totalCentDiscount,
    unitCentDiscountPrice,
  };
};

export const calculateTriflexLengthPrice = (
  pieces: number,
  unitCentPrice: number,
  length: number,
  quantityDiscounts?: PriceFragmentEnhanced[],
  lengthUnitCodeForUnitSystem?: UnitCode
): TotalProductPrice => {
  if (
    !Number.isInteger(pieces) ||
    pieces < E_CHAIN_MIN_BUY_QUANTITY ||
    pieces > E_CHAIN_MAX_BUY_QUANTITY ||
    length < E_CHAIN_MIN_BUY_LENGTH ||
    length > E_CHAIN_MAX_BUY_LENGTH
  ) {
    return {};
  }

  switch (lengthUnitCodeForUnitSystem) {
    case LengthCode.MILLIMETER:
      return calculateMetricPrice({
        pieces,
        length_mm: length,
        unitCentPricePerMeter: unitCentPrice,
        quantityDiscounts,
      });

    case LengthCode.FEET:
      return calculateImperialPrice({
        pieces,
        length_ft: length,
        unitCentPricePerFoot: unitCentPrice,
        quantityDiscounts,
      });

    default:
      throw new Error(`Unknown length unit: ${lengthUnitCodeForUnitSystem}`);
  }
};

export const calculateTotalProductPrice = (
  priceType: GraphQlPriceType,
  units: number,
  pieces: number,
  unitBasePriceInCent: number,
  unitLengthPriceInCent?: number,
  quantityDiscounts?: PriceFragmentEnhanced[],
  lengthConfig?: LengthConfig,
  links?: number,
  lengthUnitCodeForUnitSystem?: UnitCode
): TotalProductPrice => {
  switch (priceType) {
    case GraphQlPriceType.LengthAndPiece:
    case GraphQlPriceType.LengthAndPieceWithoutCopper:
      return calculateTotalLengthPiecePrice(
        priceType,
        units,
        unitBasePriceInCent,
        pieces,
        unitLengthPriceInCent,
        quantityDiscounts
      );
    case GraphQlPriceType.LeadScrewLength:
    case GraphQlPriceType.Piece:
    case GraphQlPriceType.PieceWithCopper:
    case GraphQlPriceType.PieceWithChainLengthDependency:
      // Piece and PieceWithCopper have the same calculation, because copper surcharge will only be notified about,
      // the actual value will be added in the checkout process
      return calculateTotalPiecePrice(unitBasePriceInCent, pieces, quantityDiscounts);
    case GraphQlPriceType.Length:
    case GraphQlPriceType.LengthWithoutCopper:
      return calculateTotalChainflexPrice(units, unitBasePriceInCent, pieces, quantityDiscounts);
    case GraphQlPriceType.LengthWithCuttingCostWithoutCopper:
      return calculateTotalDrylinTPrice(units, unitBasePriceInCent, pieces, lengthConfig, quantityDiscounts);
    case GraphQlPriceType.TribotapeLength:
      return calculateTotalTribotapePrice(units, unitBasePriceInCent, pieces, lengthConfig, quantityDiscounts);
    case GraphQlPriceType.ChainLink:
      return calculateTotalTriflexPrice(unitBasePriceInCent, pieces, quantityDiscounts, links);
    case GraphQlPriceType.ChainLength:
      return calculateTriflexLengthPrice(
        pieces,
        unitBasePriceInCent,
        units,
        quantityDiscounts,
        lengthUnitCodeForUnitSystem
      );
    default:
      throw new Error('No calculation function found');
  }
};

export const overwritePricesForAlternativeAndSuitableProps = (
  products: AlternativeProductProps[] | SuitableProductProps[],
  prices: SharedProductPagePropsPrices
): (AlternativeProductProps | SuitableProductProps)[] => {
  const currentProducts = [...products];
  currentProducts.forEach(product => {
    const newPrice = prices.associatedProductPrices?.find(p => p.id === product.productId);
    if (!newPrice) {
      return product;
    }

    const productWithNewPrice = product;
    productWithNewPrice.prices = {
      currency: newPrice?.quantityDiscounts?.[0]?.currency_code || '',
      regularPrice: newPrice?.quantityDiscounts?.[0]?.price || 0,
    };
    return productWithNewPrice;
  });

  return currentProducts;
};

export type TotalEChainProductPriceObject = {
  priceQuantityUnit?: PriceQuantityUnit;
  unitCentDiscountPrice?: number;
  totalCentDiscountPriceIncludingPartList?: number;
  singleCentDiscountPrice?: number;
};

type PriceCalculationValues = {
  units: number;
  pieces: number;
  kettengliedCentPrice: number;
  numKettenGlieder: number;
  quantityDiscounts?: PriceFragmentEnhanced[];
};

function calcEChainPartListTotalDiscountPrice(echainPartsListItems: EchainPartsListItem[] | undefined): number {
  let echainTotalDiscountPrice = 0;

  if (!echainPartsListItems || echainPartsListItems.length === 0) {
    return echainTotalDiscountPrice;
  }

  echainPartsListItems.every(item => {
    // If there is at least one item without a price the complete e-chain configuration is "upon request"/has no price
    if (item.totalPrice <= 0) {
      echainTotalDiscountPrice = -1;
      return false; // stop iterating
    }
    // HINT: The totalPrice is already multiplied by quantity and the discount is considered as well.
    // Single piece price is in the item.prices object
    const itemPrice = convertPriceToCent(item.totalPrice);
    echainTotalDiscountPrice += itemPrice;

    return true; // default return value to keep every() running
  });

  return echainTotalDiscountPrice;
}

/**
 * calculates the prices for the e-chain itself. This does not include any part list item.
 * returns an object containing prices with and without discounts and the total discount
 * @return {
 *   totalCentPrice?  total price in cent without any discount
 *   totalCentDiscountPrice?  total price in cent based on the quantity discount price levels (Staffelpreise) and system amount
 *   singleCentDiscountPrice total price in cent based on the quantity discount price levels for a single system
 *   totalCentDiscount?  total discount in cent
 *   unitCentDiscountPrice?  unit price with quantity discount
 * }
 */
function calculateEChainPrice(
  units: number,
  kettengliedCentPrice: number,
  pieces: number,
  numKettenGlieder: number,
  quantityDiscounts?: PriceFragmentEnhanced[]
): {
  totalCentPrice?: number;
  totalCentDiscountPrice?: number;
  singleCentDiscountPrice?: number;
  totalCentDiscount?: number;
  unitCentDiscountPrice?: number;
} {
  if (
    !Number.isInteger(pieces) ||
    numKettenGlieder < E_CHAIN_MIN_BUY_LENGTH ||
    numKettenGlieder > E_CHAIN_MAX_BUY_LENGTH ||
    pieces < E_CHAIN_MIN_BUY_QUANTITY ||
    pieces > E_CHAIN_MAX_BUY_QUANTITY
  ) {
    return {};
  }

  // find price per unit of correct discount level (=Staffelpreis)
  const discountTier = quantityDiscounts
    ? [...quantityDiscounts]
        .sort(sortByParam<PriceFragmentEnhanced>(item => item.discount_threshold, true))
        .find(
          item =>
            (item.discount_threshold !== undefined && item.discount_threshold !== null
              ? item.discount_threshold
              : Number.MAX_VALUE) <= units
        )
    : undefined;
  const quantityLevelCentPrice =
    discountTier !== undefined && discountTier.price
      ? (discountTier.price > 0 ? discountTier.price : 0) * 100
      : undefined;

  const unitCentDiscountPrice = Math.round(
    quantityLevelCentPrice !== undefined ? quantityLevelCentPrice : kettengliedCentPrice
  );
  const totalCentPrice = Math.round(kettengliedCentPrice * units * pieces);
  const singleCentDiscountPrice = unitCentDiscountPrice ? Math.round(unitCentDiscountPrice * units) : undefined;
  const totalCentDiscountPrice = unitCentDiscountPrice ? Math.round(unitCentDiscountPrice * units * pieces) : undefined;
  const totalCentDiscount = totalCentDiscountPrice ? totalCentPrice - totalCentDiscountPrice : undefined;

  return { totalCentPrice, singleCentDiscountPrice, totalCentDiscountPrice, totalCentDiscount, unitCentDiscountPrice };
}

/**
 * calculates the total prices for an e-chain product. This includes all part list items.
 * returns an object containing prices with and without discounts and the total discount
 * @return {
 *   totalCentPrice?  total price in cent without any discount
 *   totalCentDiscountPrice?  total price in cent based on the quantity discount price levels (Staffelpreise) and system amount
 *   singleCentDiscountPrice total price in cent based on the quantity discount price levels for a single system
 *   totalCentDiscount?  total discount in cent
 *   unitCentDiscountPrice?  unit price with quantity discount
 * }
 */
export function calculateTotalEChainPrice(
  calculationValues: PriceCalculationValues,
  echainPartsListItems?: EchainPartsListItem[]
): TotalEChainProductPriceObject {
  const { units, numKettenGlieder, kettengliedCentPrice, pieces, quantityDiscounts } = calculationValues;
  const priceQuantityUnit = quantityDiscounts?.[0]?.unit;
  const eChainPrices = calculateEChainPrice(units, kettengliedCentPrice, pieces, numKettenGlieder, quantityDiscounts);
  if (!eChainPrices || !eChainPrices.totalCentPrice) {
    return {
      unitCentDiscountPrice: eChainPrices.unitCentDiscountPrice,
      singleCentDiscountPrice: eChainPrices.singleCentDiscountPrice,
    };
  }

  if (!echainPartsListItems || echainPartsListItems.length === 0) {
    return {
      priceQuantityUnit,
      unitCentDiscountPrice: eChainPrices.unitCentDiscountPrice,
      totalCentDiscountPriceIncludingPartList: eChainPrices.totalCentDiscountPrice,
      singleCentDiscountPrice: eChainPrices.singleCentDiscountPrice,
    };
  }

  // Add the prices of the parts list items to the total price
  const echainPartListTotalDiscountPrice = calcEChainPartListTotalDiscountPrice(echainPartsListItems);

  const totalCentDiscountPriceIncludingPartList =
    eChainPrices.totalCentDiscountPrice && echainPartListTotalDiscountPrice > -1
      ? Math.round(eChainPrices.totalCentDiscountPrice + pieces * echainPartListTotalDiscountPrice)
      : undefined;

  return {
    priceQuantityUnit,
    unitCentDiscountPrice: eChainPrices.unitCentDiscountPrice,
    totalCentDiscountPriceIncludingPartList,
    singleCentDiscountPrice: eChainPrices.singleCentDiscountPrice,
  };
}

export const calculateTotalEChainProductPrice = (
  priceType: GraphQlPriceType,
  calculationValues: PriceCalculationValues,
  echainPartsListItems?: EchainPartsListItem[]
): TotalEChainProductPriceObject => {
  switch (priceType) {
    case GraphQlPriceType.ChainLink:
    case GraphQlPriceType.ChainLength:
      return calculateTotalEChainPrice(calculationValues, echainPartsListItems);

    case GraphQlPriceType.Length:
    case GraphQlPriceType.LengthWithoutCopper:
    case GraphQlPriceType.LengthAndPiece:
    case GraphQlPriceType.LengthAndPieceWithoutCopper:
    case GraphQlPriceType.Piece:
    case GraphQlPriceType.PieceWithCopper:
    case GraphQlPriceType.PieceWithChainLengthDependency:
    case GraphQlPriceType.LengthWithCuttingCostWithoutCopper:
    case GraphQlPriceType.LeadScrewLength:
    case GraphQlPriceType.TribotapeLength:
      throw new Error(`Failed! Given PriceType ${priceType} is not supported for Echain price calculation.`);
  }
};
